import { Component, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { LayerFeaturePanelService } from "../../../../shared-components/feature-table";
import { Viewerlayer } from "../../../model/viewerlayer";
import { buildLayerDescriptor, hasLayerFeatureData } from "../../../utils/layer-descriptor.util";
import { LayerFeatureFilter } from "../../../model/layer-feature-filter";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { EditLayerDialogComponent } from "../../../../dialog-components/edit-layer-dialog/edit-layer-dialog.component";
import { EditVectorStyleDialogComponent } from "../../../../dialog-components/edit-vector-style-dialog/edit-vector-style-dialog.component";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";
import { MatDialog } from "@angular/material/dialog";
import { OverlayDialogComponent } from "../../../../dialog-components/overlay-dialog/overlay-dialog.component";
import { ViewerConfigurationService } from "../../../services/viewer-configuration.service";
import { MapService } from "../../../services/internal-services/map.service";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../../services/internal-services/viewer-services/inject-base-viewer.service";
import { VlmLayerPanelService } from "../../../../shared-components/vlm-layer-panel/vlm-layer-panel.service";
import { PipelineEmbeddingsPanelService } from "../../../../shared-components/pipeline-embeddings-panel/pipeline-embeddings-panel.service";
import { StyleLegendService } from "../../../../shared/services/style-legend.service";
import { IVectorStyle } from "../../../../shared/model/vector-style-definition.interface";
import { deriveMetadataFromRules } from "../../../../shared/utils/geojson-style-converter";
import { FeatureFlags } from "../../../model/feature-flags";
import { FeatureFlagsService } from "../../../services/internal-services/feature-flags.service";
@Component({
  selector: "phoenix-user-layer-data",
  templateUrl: "./user-layer-data.component.html",
  styleUrls: ["./user-layer-data.component.scss"],
  standalone: false,
})
export class UserLayerDataComponent implements OnInit {
  viewerService = inject(VIEWER_SERVICE);

  flagService = inject(FeatureFlagsService);

  protected readonly FeatureFlags = FeatureFlags;

  private readonly vlmLayerPanelService = inject(VlmLayerPanelService);

  private readonly pipelineEmbeddingsPanelService = inject(PipelineEmbeddingsPanelService);

  private readonly styleLegendService = inject(StyleLegendService);

  // TODO: we have to replace by openlayer layer object to has use and avoid to wait updates or refresh
  // we should show OpenList view always by class type, not make sense use only if has features, because we have to wait until get features
  private _layer: Viewerlayer;

  private _layerFeatureFilter: LayerFeatureFilter;

  private _focusedLayer: Viewerlayer;

  private _focusedFeature: any;

  sources = ViewerConstants.SOURCE_NAME;

  usedColors: [];

  @Input() multiSelectMode: boolean = false;

  @Input() selected: boolean = false;

  @Input() isChild: boolean = false;

  expandLayerFeatures: boolean = false;

  expandedGroup: boolean = false;

  additionalFeatures: any[];

  private _selectFeature: any;

  highlightLayer: boolean = false;

  featureIndex: number = 0;

  focus: boolean = false;

  isDebugMode = false;

  get layer(): Viewerlayer {
    return this._layer;
  }

  @Input() set selectedFeature(feature: any) {
    if (feature) {
      this.selectFeature(feature);
    } else {
      this._selectFeature = feature;
      this.expandLayerFeatures = false;
    }
  }

  get selectedFeature() {
    return this._selectFeature;
  }

  @Input() set layer(value: Viewerlayer) {
    this._layer = value;
  }

  get features() {
    return this.layer?.source?.features?.features || [];
  }

  get isGroupLayer(): boolean {
    return this.layer?.layerType === ViewerConstants.LAYER_TYPE.GROUPLAYER;
  }

  get layerFeatureFilter(): LayerFeatureFilter {
    return this._layerFeatureFilter;
  }

  @Input() set layerFeatureFilter(value: LayerFeatureFilter) {
    this._layerFeatureFilter = value;
    this.checkHighlightLayer();
  }

  get focusedLayer(): Viewerlayer {
    return this._focusedLayer;
  }

  @Input() set focusedLayer(value: Viewerlayer) {
    this._focusedLayer = value;
    this.focus = this.layer === this.focusedLayer;
  }

  get focusedFeature(): any {
    return this._focusedFeature;
  }

  @Input() set focusedFeature(value: any) {
    this._focusedFeature = value;
  }

  @Output() selectLayer = new EventEmitter<Viewerlayer>();

  @Output() selectFeatureEvent = new EventEmitter<any>();

  @Output() onFocusLayer = new EventEmitter<any>();

  @Output() retrieveDamageAssessmentEvent = new EventEmitter<Viewerlayer>();

  @Output() retrievePropertyInformationEvent = new EventEmitter<Viewerlayer>();

  private readonly layerFeaturePanel = inject(LayerFeaturePanelService);

  constructor(
    protected matDialog: MatDialog,
    protected googleAnalyticsService: GoogleAnalyticsService,
    public mapService: MapService,
    public viewerConfigurationService: ViewerConfigurationService,
  ) {}

  ngOnInit(): void {
    this.viewerService.developerTools$.subscribe((enable: boolean) => {
      this.isDebugMode = enable;
    });
  }

  checkHighlightLayer() {
    this.highlightLayer = this.layerFeatureFilter && this._layer.name === this.layerFeatureFilter.layerFilter;
    this.expandLayerFeatures = !!this.layerFeatureFilter;
  }

  checkEditAvailable() {
    return (
      this._layer &&
      !this.isGroupLayer &&
      [
        this.sources.WFS,
        this.sources.VECTOR,
        this.sources.VECTORTILES,
        this.sources.ARCGISFEATURE,
        this.sources.XYZ,
        this.sources.WMS,
        this.sources.WMTS,
      ].includes(this._layer.sourceName) &&
      this.layer.owner
    );
  }

  toggleGroupExpand(): void {
    this.expandedGroup = !this.expandedGroup;
  }

  changeVisibility(event: MatCheckboxChange) {
    if (this.isChild) {
      this.viewerService.changeGroupChildVisibility(this.layer.id, event.checked);
    } else {
      this.viewerService.changeUserLayerVisibility(this.layer.id, event.checked);
      this.viewerService.updateUserLayer(this.layer);
      this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
    }
  }

  editLayer() {
    const dialog = this.matDialog.open(EditLayerDialogComponent, {
      data: {
        layer: this.layer,
        usedColors: this.usedColors,
      },
      minWidth: "350px",
      autoFocus: false,
    });

    dialog.afterClosed().subscribe((result) => {
      if (result && result.status === 200) {
        this.layer.style = JSON.stringify(result.style);
        this.layer.opacity = result.opacity;
        this.layer.additionalStyles = result.additionalStyles;
        this.viewerService.updateUserLayerStyle(this.layer);
        this.viewerService.updateUserLayers(this.viewerService.getUserLayers());
        if (this.selectedFeature) {
          this.viewerService.setDefaultFeaturesStyle([this.selectedFeature]);
        }
      }
      this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
    });
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_EDIT_STYLE,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  /** Debug-only: edit the layer's advanced flat/rules `styleDefinition` (separate from the basic fill/stroke edit above). */
  editAdvancedStyle(): void {
    const dialog = this.matDialog.open(EditVectorStyleDialogComponent, {
      data: { layer: this.layer },
      minWidth: "800px",
      maxWidth: "95vw",
      autoFocus: false,
    });

    dialog.afterClosed().subscribe((result) => {
      if (result?.status === 200) {
        const styleDefinition: IVectorStyle = result.styleDefinition;
        const metadata = styleDefinition.type === "rules" ? deriveMetadataFromRules(styleDefinition.rules) : undefined;

        this.viewerService.updateLayerStyleDefinition(this.layer, styleDefinition, metadata).subscribe();

        if (this.styleLegendService.state()?.layerName === this.layer?.name) {
          this.styleLegendService.show(this.layer.id, this.layer.name, styleDefinition, metadata);
        }
      }
    });
  }

  /** Debug-only: shows the floating legend for the layer's current `styleDefinition`, if any. */
  showLegend(): void {
    if (!this.layer?.styleDefinition) return;
    this.styleLegendService.show(this.layer.id, this.layer.name, this.layer.styleDefinition, this.layer.styleMetadata);
  }

  removeLayer() {
    const dialog = this.matDialog.open(OverlayDialogComponent, {
      data: {
        text: "Are you sure you want to remove this layer?",
        isConfirmation: true,
      },
    });

    const layerId = this.layer.id;

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.removeFootprints();
        if (this.isChild) {
          this.viewerService.removeGroupChild(layerId);
        } else {
          this.viewerService.removeUserLayer(layerId);
          this.selectedFeature = null;
          this.selectFeatureEvent.emit(null);
          this.selectLayer.emit(null);
        }
      }
    });
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_REMOVE,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  removeFootprints() {
    this.viewerService.removeFeatureFootprints(this.features);
  }

  fit() {
    const wkt = this.layer.bbox;
    const options = {
      padding: [10, 10, 10, 10],
      duration: 0,
    };

    // Check if bbox its polygon or point
    if (wkt.includes("POINT")) {
      this.mapService.setCenterToWktPoint(wkt);
    } else {
      this.mapService.fitToWktPolygon(wkt, ViewerConstants.PROJECTIONS.WebMercator, options);
    }
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_HIGHLIGHT_ON_MAP,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  moveBack() {
    if (this.selectedFeature) {
      this.selectedFeature = null;
      this.expandLayerFeatures = true;
    } else {
      this.expandLayerFeatures = false;
      this.selectLayer.emit(null);
      this.selectFeatureEvent.emit(null);
    }
  }

  expandFeatureslist() {
    this.expandLayerFeatures = true;
    this.selectLayer.emit(this._layer);
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_OPEN_LIST_VIEW,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  selectFeature(feature) {
    let layerFeature;
    if (feature.properties?.featureRef) {
      layerFeature = this.features.find((f) => f.id === feature.properties?.featureRef);
      this.additionalFeatures = [feature];
    } else if (this.features?.length) {
      layerFeature = this.features.find((f) => f.id === feature.id);
      this.additionalFeatures = [];
    }
    if (layerFeature) {
      const now = new Date();
      if (layerFeature.propertyInfo && layerFeature.propertyInfo.expiration) {
        const expiration = new Date(layerFeature.propertyInfo.expiration);
        if (expiration < now) {
          layerFeature.propertyInfo = null;
        }
      }
      if (layerFeature.damageInfo && layerFeature.damageInfo.expiration) {
        const expiration = new Date(layerFeature.damageInfo.expiration);
        if (expiration < now) {
          layerFeature.damageInfo = null;
        }
      }
      layerFeature.point = feature.point;

      this.features.map((f) => (f.focus = false));

      this.viewerService.zoomToFeature(this.layer, layerFeature);

      layerFeature.focus = true;
      this.focusedFeature = layerFeature;

      this._selectFeature = layerFeature;
      this.featureIndex = this.layer.source.features.features.findIndex((f) => f.id === layerFeature.id);
    }
    this.selectLayer.emit(this._layer);
  }

  onSelectedFeature($event: any) {
    const feature = $event;
    this.onFocusLayer.emit({ layer: this.layer, feature: feature });
    this.selectFeature(feature);
  }

  previousFeature() {
    this.featureIndex--;
    const feature = this.features[this.featureIndex];
    this.selectFeature(feature);
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_PREVIOUS_LOCATION_ARROW,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  nextFeature() {
    this.featureIndex++;
    const feature = this.features[this.featureIndex];
    this.selectFeature(feature);
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_NEXT_LOCATION_ARROW,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  hasGeoJsonFeatures(): boolean {
    return (this.features?.length ?? 0) > 0;
  }

  viewLayerData(): void {
    if (!this.hasGeoJsonFeatures()) return;
    const siblings = this.viewerService.getUserLayers().filter(hasLayerFeatureData);
    const groups = this.viewerService
      .getUserLayers()
      .filter((layer) => layer.layerType === ViewerConstants.LAYER_TYPE.GROUPLAYER);

    const childLayers = groups.flatMap((group) => group.childs?.filter(hasLayerFeatureData) ?? []);
    const featureLayers = [...siblings, ...childLayers];

    const startIndex = Math.max(
      featureLayers.findIndex((layer) => layer.id === this.layer.id),
      0,
    );
    const layers = featureLayers.map((layer) => buildLayerDescriptor(layer, this.viewerService));

    this.layerFeaturePanel.open({ layers, startIndex });
  }

  retrieveDamageAssessment() {
    this.retrieveDamageAssessmentEvent.emit(this.layer);
  }

  retrievePropertyInformation() {
    this.retrievePropertyInformationEvent.emit(this.layer);
  }

  checkPIF() {
    return this.layer?.overlayType === "csv";
  }

  openVlmPanel(): void {
    const features = this.features;
    if (!features?.length) return;
    this.vlmLayerPanelService.register({ layerName: this.layer.name, features });
    this.vlmLayerPanelService.open();
  }

  openEmbeddingsPanel(): void {
    const features = this.features;
    if (!features?.length) return;
    this.pipelineEmbeddingsPanelService.register({ layerName: this.layer.name, features });
    this.pipelineEmbeddingsPanelService.open();
  }

  getFillColorLayer() {
    return this.layer?.style ? JSON.parse(this.layer.style).defaultSetting.colorFill : "rgb(46, 111, 255)";
  }
}
